'use client';

import { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Modal,
    Stack,
    Group,
    TextInput,
    ActionIcon,
    Title,
    Select,
    Switch,
    Textarea,
    Accordion,
    Box,
} from '@mantine/core';
import { IconTrash, IconPlus } from '@tabler/icons-react';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { Dropzone } from '../EditorText/Dropzone';
import { usePathname, useRouter } from 'next/navigation';
import { i18n } from '@/i18n-config';
import { addDeploymentToast } from '@/src/helpers/add-deployment-tip';
import { ColorField } from '../ColorField';

type LegacyLinkItem = {
    label: string;
    link: string;
    icon?: string;
    links?: LegacyLinkItem[];
};

type HeaderLink = {
    label: string;
    href: string;
    description?: string;
};

type HeaderColumn = {
    title: string;
    links: HeaderLink[];
};

type HeaderNavItem = {
    label: string;
    href?: string;
    columns?: HeaderColumn[];
};

type SurheaderItem = {
    icon?: string;
    href: string;
    label: string;
};

type HeaderAuth = {
    dashboardLabel: string;
    dashboardHref: string;
    loginLabel: string;
    loginHref: string;
    logoutLabel: string;
    afterLogoutHref?: string;
};

type HeaderCta = {
    label: string;
    href: string;
};

type ImportantMessage = {
    text: string;
    color?: string;
    textColor?: string;
};

type Props = {
    // legacy support
    metaHeader?: LegacyLinkItem[];
    mainHeader?: LegacyLinkItem[];

    // new static header support
    metaItems?: SurheaderItem[];
    navItems?: HeaderNavItem[];
    cta?: HeaderCta;
    auth?: HeaderAuth;
    importantMessage?: ImportantMessage;
};

type AvailableDropdown = {
    value: string;
    label: string;
};

function normalizeMeta(
    legacy?: LegacyLinkItem[],
    modern?: SurheaderItem[],
): SurheaderItem[] {
    if (modern?.length) return structuredClone(modern);
    if (!legacy?.length) return [];

    return legacy.map((item) => ({
        label: item.label ?? '',
        href: item.link ?? '',
        icon: item.icon ?? '',
    }));
}

function normalizeNav(
    legacy?: LegacyLinkItem[],
    modern?: HeaderNavItem[],
): HeaderNavItem[] {
    if (modern?.length) return structuredClone(modern);
    if (!legacy?.length) return [];

    return legacy.map((item) => ({
        label: item.label ?? '',
        href: item.link ?? '',
        columns: item.links?.length
            ? [
                {
                    title: item.label ?? '',
                    links: item.links.map((sub) => ({
                        label: sub.label ?? '',
                        href: sub.link ?? '',
                        description: '',
                    })),
                },
            ]
            : undefined,
    }));
}

function getSafeAuth(auth?: HeaderAuth): HeaderAuth {
    return {
        dashboardLabel: auth?.dashboardLabel ?? '',
        dashboardHref: auth?.dashboardHref ?? '',
        loginLabel: auth?.loginLabel ?? '',
        loginHref: auth?.loginHref ?? '',
        logoutLabel: auth?.logoutLabel ?? '',
        afterLogoutHref: auth?.afterLogoutHref ?? '',
    };
}

function getSafeImportantMessage(
    importantMessage?: ImportantMessage,
): ImportantMessage {
    return {
        text: importantMessage?.text ?? '',
        color: importantMessage?.color ?? '#ead7cf',
        textColor: importantMessage?.textColor ?? '#000000',
    };
}

export default function HeaderEditorModal({
    metaHeader,
    mainHeader,
    metaItems,
    navItems,
    cta,
    auth,
    importantMessage,
}: Props) {
    const [opened, setOpened] = useState(false);
    const [loading, setLoading] = useState(false);
    const dictionary = useDictionary();
    const pathname = usePathname();
    const router = useRouter();

    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    const locale =
        i18n.locales.find((loc) => pathname.startsWith(`/${loc}`)) ||
        i18n.defaultLocale;

    const [meta, setMeta] = useState<SurheaderItem[]>([]);
    const [nav, setNav] = useState<HeaderNavItem[]>([]);
    const [ctaState, setCtaState] = useState<HeaderCta>({
        label: '',
        href: '',
    });
    const [authState, setAuthState] = useState<HeaderAuth>(getSafeAuth(auth));
    const [important, setImportant] = useState<ImportantMessage>(
        getSafeImportantMessage(importantMessage),
    );
    const [logoUrl, setLogoUrl] = useState<string | null>(dictionary.logoUrl);

    const [slug, setSlug] = useState('');
    const [description, setDescription] = useState('');
    const [isDropdown, setIsDropdown] = useState(false);
    const [selectedMenuLink, setSelectedMenuLink] = useState<string | null>(null);
    const [createDropdown, setCreateDropdown] = useState(false);
    const [newDropdownLabel, setNewDropdownLabel] = useState('');

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const res = await fetch('/api/validate-token', {
                    method: 'GET',
                    credentials: 'include',
                });

                console.log('Auth validation response:', res);

                if (!res.ok) {
                    setIsAdmin(false);
                    setIsSuperAdmin(false);
                    return;
                }

                const data = await res.json();

                console.log('Auth validation result:', data);

                if (data?.valid && data?.user?.role === 'superAdmin') {
                    setIsSuperAdmin(true);
                    setIsAdmin(false);
                } else if (data?.valid && data?.user?.role === 'admin') {
                    setIsAdmin(true);
                    setIsSuperAdmin(false);
                } else {
                    setIsAdmin(false);
                    setIsSuperAdmin(false);
                }
            } catch (err) {
                console.error('Auth validation failed', err);
                setIsAdmin(false);
                setIsSuperAdmin(false);

                await fetch('/api/logout', {
                    method: 'POST',
                    credentials: 'include',
                });

                router.push('/login');
                router.refresh();
            }
        };

        checkAuth();
    }, [router]);

    useEffect(() => {
        setMeta(normalizeMeta(metaHeader, metaItems));
    }, [metaHeader, metaItems]);

    useEffect(() => {
        setNav(normalizeNav(mainHeader, navItems));
    }, [mainHeader, navItems]);

    useEffect(() => {
        setCtaState({
            label: cta?.label ?? '',
            href: cta?.href ?? '',
        });
    }, [cta]);

    useEffect(() => {
        setAuthState(getSafeAuth(auth));
    }, [auth]);

    useEffect(() => {
        setImportant(getSafeImportantMessage(importantMessage));
    }, [importantMessage]);

    useEffect(() => {
        setLogoUrl(dictionary.logoUrl);
    }, [dictionary.logoUrl]);

    const pageOptions = useMemo(() => {
        return Object.entries(dictionary.pages ?? {}).map(([key, page]) => {
            // @ts-ignore
            const link = page?.link || `/${key}`;
            // @ts-ignore
            const label = page?.label || key;
            return {
                value: link,
                label: `${key} (${label})`,
            };
        });
    }, [dictionary.pages]);

    const availableDropdowns = nav
        .filter((item) => Array.isArray(item.columns) && item.columns.length > 0)
        .map(
            (item) =>
                ({
                    value: item.label.toLowerCase().replace(/\s+/g, '-'),
                    label: item.label,
                }) as AvailableDropdown,
        );

    const handleSave = async () => {
        const payload = {
            locale,
            logoUrl,

            // old payload keys for backward compatibility
            metaHeader: meta.map((item) => ({
                label: item.label,
                link: item.href,
                icon: item.icon,
            })),
            header: nav.map((item) => ({
                label: item.label,
                link: item.href ?? '',
                links:
                    item.columns?.flatMap((col) =>
                        col.links.map((link) => ({
                            label: link.label,
                            link: link.href,
                        })),
                    ) ?? [],
            })),

            // new payload for StaticHeader
            staticHeader: {
                type: 'header',
                logoSrc: logoUrl,
                logoAlt: dictionary.compagnyName,
                meta,
                nav,
                cta: ctaState.label || ctaState.href ? ctaState : undefined,
                auth: authState,
                supportedLanguage: dictionary.header?.supportedLanguage,
                selectedLanguage: locale,
                important_message: important,
            },
        };

        const res = await fetch('/api/update-header', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });

        if (res.ok) {
            const { hash } = await res.json();
            addDeploymentToast(
                'Déploiement du header en cours. Optimisation des fichiers...',
                locale,
                hash,
            );
            setOpened(false);
        } else {
            alert('Erreur lors de la sauvegarde');
        }
    };

    const handleCreatePage = async () => {
        setLoading(true);

        try {
            const res = await fetch('/api/generate-page', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    slug,
                    description,
                    targetMenuKey: isDropdown ? selectedMenuLink || slug : slug,
                    createDropdown,
                    newDropdownLabel: createDropdown ? newDropdownLabel : undefined,
                }),
            });

            const result = await res.json();

            if (res.ok) {
                addDeploymentToast(
                    'Déploiement du header en cours. Optimisation des fichiers...',
                );
                setOpened(false);
            } else {
                alert(result.error || 'Erreur lors de la création de la page');
            }
        } catch (e) {
            alert('Erreur serveur');
        } finally {
            setLoading(false);
        }
    };

    const updateMeta = (
        index: number,
        key: keyof SurheaderItem,
        value: string,
    ) => {
        const updated = structuredClone(meta);
        updated[index][key] = value as never;
        setMeta(updated);
    };

    const addMeta = () => {
        setMeta([...meta, { label: '', href: '', icon: '' }]);
    };

    const removeMeta = (index: number) => {
        setMeta(meta.filter((_, i) => i !== index));
    };

    const updateNavItem = (
        index: number,
        key: keyof HeaderNavItem,
        value: string,
    ) => {
        const updated = structuredClone(nav);
        updated[index][key] = value as never;
        setNav(updated);
    };

    const addNavItem = () => {
        setNav([...nav, { label: '', href: '' }]);
    };

    const removeNavItem = (index: number) => {
        setNav(nav.filter((_, i) => i !== index));
    };

    const addColumn = (navIndex: number) => {
        const updated = structuredClone(nav);
        if (!updated[navIndex].columns) updated[navIndex].columns = [];
        updated[navIndex].columns!.push({ title: '', links: [] });
        setNav(updated);
    };

    const updateColumnTitle = (
        navIndex: number,
        colIndex: number,
        value: string,
    ) => {
        const updated = structuredClone(nav);
        if (!updated[navIndex].columns) return;
        updated[navIndex].columns![colIndex].title = value;
        setNav(updated);
    };

    const removeColumn = (navIndex: number, colIndex: number) => {
        const updated = structuredClone(nav);
        if (!updated[navIndex].columns) return;
        updated[navIndex].columns!.splice(colIndex, 1);
        if (updated[navIndex].columns?.length === 0) {
            delete updated[navIndex].columns;
        }
        setNav(updated);
    };

    const addColumnLink = (navIndex: number, colIndex: number) => {
        const updated = structuredClone(nav);
        if (!updated[navIndex].columns) return;
        updated[navIndex].columns![colIndex].links.push({
            label: '',
            href: '',
            description: '',
        });
        setNav(updated);
    };

    const updateColumnLink = (
        navIndex: number,
        colIndex: number,
        linkIndex: number,
        key: keyof HeaderLink,
        value: string,
    ) => {
        const updated = structuredClone(nav);
        if (!updated[navIndex].columns) return;
        updated[navIndex].columns![colIndex].links[linkIndex][key] = value as never;
        setNav(updated);
    };

    const removeColumnLink = (
        navIndex: number,
        colIndex: number,
        linkIndex: number,
    ) => {
        const updated = structuredClone(nav);
        if (!updated[navIndex].columns) return;
        updated[navIndex].columns![colIndex].links.splice(linkIndex, 1);
        setNav(updated);
    };

    return (
        <>
            {(isSuperAdmin || isAdmin) && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginBottom: '1rem',
                        position: 'relative',
                        zIndex: 1000,
                    }}
                >
                    <Button onClick={() => setOpened(true)} size="xs" color="black">
                        Modifier Header
                    </Button>
                </div>
            )}

            <Modal
                opened={opened}
                onClose={() => setOpened(false)}
                title="Modifier le menu"
                size="xl"
                zIndex={2000}
            >
                <Accordion variant="contained" defaultValue="update">
                    <Accordion.Item value="update">
                        <Accordion.Control>🛠 Modifier le header</Accordion.Control>
                        <Accordion.Panel>
                            <Stack>
                                <Title order={4}>Logo du site</Title>
                                <Dropzone imageUrl={logoUrl} setImageUrl={setLogoUrl} />

                                <Title order={4}>Meta Header</Title>
                                {meta.map((item, i) => (
                                    <Group key={`meta-${i}`} align="start">
                                        <TextInput
                                            label="Label"
                                            value={item.label}
                                            onChange={(e) =>
                                                updateMeta(i, 'label', e.currentTarget.value)
                                            }
                                        />
                                        <TextInput
                                            label="Lien"
                                            value={item.href}
                                            onChange={(e) =>
                                                updateMeta(i, 'href', e.currentTarget.value)
                                            }
                                        />
                                        <TextInput
                                            label="Icône"
                                            value={item.icon || ''}
                                            onChange={(e) =>
                                                updateMeta(i, 'icon', e.currentTarget.value)
                                            }
                                        />
                                        <ActionIcon color="red" onClick={() => removeMeta(i)}>
                                            <IconTrash />
                                        </ActionIcon>
                                    </Group>
                                ))}
                                <Button
                                    leftSection={<IconPlus size={16} />}
                                    onClick={addMeta}
                                    variant="light"
                                >
                                    Ajouter un élément au metaHeader
                                </Button>

                                <Title order={4}>Menu principal</Title>
                                {nav.map((item, i) => (
                                    <Stack
                                        key={`nav-${i}`}
                                        p="sm"
                                        style={{ border: '1px solid #eee', borderRadius: 8 }}
                                    >
                                        <Group align="start">
                                            <TextInput
                                                label="Label"
                                                value={item.label}
                                                onChange={(e) =>
                                                    updateNavItem(i, 'label', e.currentTarget.value)
                                                }
                                            />

                                            <Box style={{ minWidth: 260, flex: 1 }}>
                                                <Stack gap={4}>
                                                    <TextInput
                                                        label="Lien direct"
                                                        placeholder="/contact"
                                                        value={item.href ?? ''}
                                                        onChange={(e) =>
                                                            updateNavItem(i, 'href', e.currentTarget.value)
                                                        }
                                                    />
                                                    <Select
                                                        label="Choisir une page existante"
                                                        placeholder="Sélectionner une page"
                                                        data={pageOptions}
                                                        value={
                                                            pageOptions.some(
                                                                (opt) => opt.value === (item.href ?? ''),
                                                            )
                                                                ? (item.href ?? '')
                                                                : null
                                                        }
                                                        onChange={(val) => updateNavItem(i, 'href', val || '')}
                                                        searchable
                                                        clearable
                                                        allowDeselect
                                                    />
                                                </Stack>
                                            </Box>

                                            <ActionIcon color="red" onClick={() => removeNavItem(i)}>
                                                <IconTrash />
                                            </ActionIcon>
                                        </Group>

                                        <Button
                                            size="xs"
                                            variant="outline"
                                            onClick={() => addColumn(i)}
                                        >
                                            ➕ Ajouter une colonne dropdown
                                        </Button>

                                        {item.columns?.map((col, j) => (
                                            <Stack
                                                key={`col-${i}-${j}`}
                                                ml="md"
                                                p="sm"
                                                style={{ border: '1px dashed #ddd', borderRadius: 8 }}
                                            >
                                                <Group align="start">
                                                    <TextInput
                                                        label="Titre colonne"
                                                        value={col.title}
                                                        onChange={(e) =>
                                                            updateColumnTitle(i, j, e.currentTarget.value)
                                                        }
                                                    />
                                                    <ActionIcon color="red" onClick={() => removeColumn(i, j)}>
                                                        <IconTrash />
                                                    </ActionIcon>
                                                </Group>

                                                {col.links.map((sub, k) => (
                                                    <Group key={`sub-${i}-${j}-${k}`} align="start">
                                                        <TextInput
                                                            label="Sous-label"
                                                            value={sub.label}
                                                            onChange={(e) =>
                                                                updateColumnLink(
                                                                    i,
                                                                    j,
                                                                    k,
                                                                    'label',
                                                                    e.currentTarget.value,
                                                                )
                                                            }
                                                        />

                                                        <Box style={{ minWidth: 260, flex: 1 }}>
                                                            <Stack gap={4}>
                                                                <TextInput
                                                                    label="Lien"
                                                                    placeholder="/groupes/entreprises"
                                                                    value={sub.href}
                                                                    onChange={(e) =>
                                                                        updateColumnLink(
                                                                            i,
                                                                            j,
                                                                            k,
                                                                            'href',
                                                                            e.currentTarget.value,
                                                                        )
                                                                    }
                                                                />
                                                                <Select
                                                                    label="Choisir une page existante"
                                                                    placeholder="Sélectionner une page"
                                                                    data={pageOptions}
                                                                    value={
                                                                        pageOptions.some(
                                                                            (opt) => opt.value === sub.href,
                                                                        )
                                                                            ? sub.href
                                                                            : null
                                                                    }
                                                                    onChange={(val) =>
                                                                        updateColumnLink(i, j, k, 'href', val || '')
                                                                    }
                                                                    searchable
                                                                    clearable
                                                                    allowDeselect
                                                                />
                                                            </Stack>
                                                        </Box>

                                                        <TextInput
                                                            label="Description"
                                                            value={sub.description || ''}
                                                            onChange={(e) =>
                                                                updateColumnLink(
                                                                    i,
                                                                    j,
                                                                    k,
                                                                    'description',
                                                                    e.currentTarget.value,
                                                                )
                                                            }
                                                        />

                                                        <ActionIcon
                                                            color="red"
                                                            onClick={() => removeColumnLink(i, j, k)}
                                                        >
                                                            <IconTrash />
                                                        </ActionIcon>
                                                    </Group>
                                                ))}

                                                <Button
                                                    size="xs"
                                                    variant="outline"
                                                    onClick={() => addColumnLink(i, j)}
                                                >
                                                    ➕ Ajouter un sous-lien
                                                </Button>
                                            </Stack>
                                        ))}
                                    </Stack>
                                ))}
                                <Button
                                    leftSection={<IconPlus size={16} />}
                                    onClick={addNavItem}
                                    variant="light"
                                >
                                    Ajouter un élément au menu principal
                                </Button>

                                <Title order={4}>CTA</Title>
                                <Group grow>
                                    <TextInput
                                        label="Texte bouton"
                                        value={ctaState.label}
                                        onChange={(e) =>
                                            setCtaState((prev) => ({
                                                ...prev,
                                                label: e.currentTarget.value,
                                            }))
                                        }
                                    />
                                    <TextInput
                                        label="Lien bouton"
                                        value={ctaState.href}
                                        onChange={(e) =>
                                            setCtaState((prev) => ({
                                                ...prev,
                                                href: e.currentTarget.value,
                                            }))
                                        }
                                    />
                                </Group>

                                <Title order={4}>Auth</Title>
                                <Stack>
                                    <Group grow>
                                        <TextInput
                                            label="Dashboard label"
                                            value={authState.dashboardLabel}
                                            onChange={(e) =>
                                                setAuthState((prev) => ({
                                                    ...prev,
                                                    dashboardLabel: e.currentTarget.value,
                                                }))
                                            }
                                        />
                                        <TextInput
                                            label="Dashboard href"
                                            value={authState.dashboardHref}
                                            onChange={(e) =>
                                                setAuthState((prev) => ({
                                                    ...prev,
                                                    dashboardHref: e.currentTarget.value,
                                                }))
                                            }
                                        />
                                    </Group>

                                    <Group grow>
                                        <TextInput
                                            label="Login label"
                                            value={authState.loginLabel}
                                            onChange={(e) =>
                                                setAuthState((prev) => ({
                                                    ...prev,
                                                    loginLabel: e.currentTarget.value,
                                                }))
                                            }
                                        />
                                        <TextInput
                                            label="Login href"
                                            value={authState.loginHref}
                                            onChange={(e) =>
                                                setAuthState((prev) => ({
                                                    ...prev,
                                                    loginHref: e.currentTarget.value,
                                                }))
                                            }
                                        />
                                    </Group>

                                    <Group grow>
                                        <TextInput
                                            label="Logout label"
                                            value={authState.logoutLabel}
                                            onChange={(e) =>
                                                setAuthState((prev) => ({
                                                    ...prev,
                                                    logoutLabel: e.currentTarget.value,
                                                }))
                                            }
                                        />
                                        <TextInput
                                            label="After logout href"
                                            value={authState.afterLogoutHref ?? ''}
                                            onChange={(e) =>
                                                setAuthState((prev) => ({
                                                    ...prev,
                                                    afterLogoutHref: e.currentTarget.value,
                                                }))
                                            }
                                        />
                                    </Group>
                                </Stack>

                                <Title order={4}>Bannière importante / News banner</Title>
                                <Stack>
                                    <Textarea
                                        label="Texte"
                                        placeholder="Texte de la bannière"
                                        value={important.text}
                                        onChange={(e) =>
                                            setImportant((prev) => ({
                                                ...prev,
                                                text: e.currentTarget.value,
                                            }))
                                        }
                                        autosize
                                        minRows={2}
                                    />

                                    <Group grow>
                                        <ColorField
                                            label="Couleur de fond"
                                            value={important.color || '#ead7cf'}
                                            onChange={(value) =>
                                                setImportant((prev) => ({ ...prev, color: value }))
                                            }
                                        />
                                        <ColorField
                                            label="Couleur du texte"
                                            value={important.textColor || '#000000'}
                                            onChange={(value) =>
                                                setImportant((prev) => ({ ...prev, textColor: value }))
                                            }
                                        />
                                    </Group>
                                </Stack>

                                <Button fullWidth mt="md" onClick={handleSave}>
                                    💾 Enregistrer
                                </Button>
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>

                    {isSuperAdmin && (
                        <Accordion.Item value="create">
                            <Accordion.Control>✨ Créer une nouvelle page</Accordion.Control>
                            <Accordion.Panel>
                                <Stack>
                                    <TextInput
                                        label="Slug de la page"
                                        placeholder="ex: notre-equipe"
                                        value={slug}
                                        onChange={(e) => setSlug(e.currentTarget.value)}
                                    />
                                    <Textarea
                                        label="Description"
                                        placeholder="Décrivez le contenu souhaité pour cette page"
                                        value={description}
                                        onChange={(e) => setDescription(e.currentTarget.value)}
                                        autosize
                                        minRows={3}
                                    />
                                    <Switch
                                        label="Ajouter dans un menu déroulant ?"
                                        checked={isDropdown}
                                        onChange={(e) => setIsDropdown(e.currentTarget.checked)}
                                    />
                                    {isDropdown && (
                                        <>
                                            <Select
                                                label="Menu déroulant cible"
                                                placeholder="Sélectionner un menu"
                                                data={availableDropdowns}
                                                value={selectedMenuLink}
                                                onChange={setSelectedMenuLink}
                                                disabled={createDropdown}
                                            />
                                            <Switch
                                                label="Créer le menu déroulant s’il n’existe pas encore"
                                                checked={createDropdown}
                                                onChange={(e) =>
                                                    setCreateDropdown(e.currentTarget.checked)
                                                }
                                            />
                                            {createDropdown && (
                                                <TextInput
                                                    label="Nom du nouveau menu déroulant"
                                                    placeholder="ex: Nos Métiers"
                                                    value={newDropdownLabel}
                                                    onChange={(e) =>
                                                        setNewDropdownLabel(e.currentTarget.value)
                                                    }
                                                    required
                                                />
                                            )}
                                        </>
                                    )}
                                    <Button
                                        loading={loading}
                                        fullWidth
                                        variant="default"
                                        onClick={handleCreatePage}
                                    >
                                        Générer la page ✨
                                    </Button>
                                </Stack>
                            </Accordion.Panel>
                        </Accordion.Item>
                    )}
                </Accordion>
            </Modal>
        </>
    );
}